import { projectSwitchPath, routeContract } from "./routeContract";
// These existing top-level routes remain accepted as legacy entry points.
// Canonical company slugs must not collide with this public route namespace.
// tenantRoute.reservedRoots.test.ts derives the roots from App.tsx and the screens
// registry; ai_engine mirrors the set as db/tenant_directory.py RESERVED_COMPANY_SLUGS.
export const RESERVED_TENANT_ROUTE_ROOTS = new Set([
    "admin",
    // The Apps page, /apps (components/getTheApp/AppsPage.tsx).
    "apps",
    "static",
    "embedpdf",
    "api",
    "authenticate",
    "c",
    "connect",
    "debug",
    "directory",
    "doc",
    "documents",
    "embed",
    "embed-auth",
    "embed-test",
    "integrations",
    "oauth",
    "rfi-links",
    "settings",
    "sheets",
    "specs",
    "drawings",
    "projects",
    "table-edit",
    "team",
    "w",
    "areas",
    "logout",
    // The public outside-party response page, /guest/:token, mounted above AppContent.
    "guest",
    // The signed-out stop page of a product update email, /email/stop/:token, mounted above AppContent.
    "email",
    // Design v2 screens (src/screens/*/register.ts); classic users are redirected from them.
    "specifications",
    "rfis",
    "submittals",
    "schedule",
    "timeline",
    "models",
    "portfolio",
    // Usage, staff engagement reporting across every project (components/usage).
    "usage",
    // Desktop-only screens; a shared link to one must not read as a company on the web.
    "offline",
    "favorites",
    "this-machine",
    // Not a route (Meetings live at /documents/meeting): a guessed /meetings shows Not Found, as
    // /specs does, instead of asking company-session about a company called "meetings".
    "meetings",
    // Dev-server module and harness pages (e.g. the desktop viewer's
    // /src/viewer/*.browser.html perf lane) are not company URLs.
    "src",
    // prime-srv's server answers these itself (src/routes/health.ts, desktopAuthCapability.ts)
    // before the client is served, so a company with one of these slugs could never load.
    "healthz",
    "readyz",
    "version",
    "desktop-auth-capability",
]);
const SLUG = /^[a-z0-9][a-z0-9_-]*$/i;

/** The URL is a selector only. Session bootstrap must admit both IDs before rendering. */
export function tenantRoute(pathname = window.location.pathname) {
    const [, companySlug, projectSlug] = pathname.split(/[?#]/, 1)[0].split("/");
    // React Router matches routes case-insensitively, so /Documents is the documents route, not a company.
    const canonical =
        companySlug &&
        SLUG.test(companySlug) &&
        !RESERVED_TENANT_ROUTE_ROOTS.has(companySlug.toLowerCase());
    const basename = canonical
        ? `/${companySlug}${projectSlug && SLUG.test(projectSlug) ? `/${projectSlug}` : ""}`
        : "";
    return {
        companySlug: canonical ? companySlug : null,
        projectSlug: canonical && projectSlug && SLUG.test(projectSlug) ? projectSlug : null,
        basename,
        pathname: basename ? pathname.slice(basename.length) || "/" : pathname,
    };
}

export function tenantPath(companySlug: string, projectSlug: string, path = "/"): string {
    const suffix = path.startsWith("/") && !path.startsWith("//") ? path : "/";
    return `/${companySlug}/${projectSlug}${suffix}`;
}

export function replaceBootstrapTenantPath(path: string): void {
    window.history.replaceState(window.history.state, "", path);
    window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
}

/** Raw browser navigation needs the prefix that React Router adds to its own links. */
export function scopedBrowserPath(path: string): string {
    // Embed and side panes pass an absolute same-origin URL to window.open.
    // Normalize it before applying the tenant prefix; foreign URLs remain
    // untouched so this helper never rewrites an external destination.
    if (/^https?:\/\//i.test(path)) {
        try {
            const url = new URL(path);
            if (url.origin !== window.location.origin) return path;
            path = `${url.pathname}${url.search}${url.hash}`;
        } catch {
            return path;
        }
    }
    if (!path.startsWith("/") || path.startsWith("//") || tenantRoute(path).basename) return path;
    if (routeContract(path)?.scope !== "project") return path;
    return `${tenantRoute().basename}${path}`;
}

/** Preserve only the fixed consent route across company/project selection. */
export function oauthTenantContinuation(): string | null {
    const route = tenantRoute();
    if (route.pathname === "/oauth/authorize") return route.pathname + window.location.search;
    const continuation = new URLSearchParams(window.location.search).get("pp_oauth_request");
    return continuation?.startsWith("/oauth/authorize?") ? continuation : null;
}

/** Company selection may require a new Document before its project list is known.
 * Carry only an established page family, never another project's entity or filters. */
export function projectPickerContinuation(): string | null {
    const route = tenantRoute();
    if (!route.companySlug || route.projectSlug || route.pathname !== "/") return null;
    const continuation = new URLSearchParams(window.location.search).get("pp_project_route");
    // Only a project page continues under the chosen project: a page with no project (Project data
    // status acts on many projects, each through its own route) must not be rebuilt under a prefix.
    return continuation &&
        routeContract(continuation)?.scope === "project" &&
        continuation === projectSwitchPath(continuation)
        ? continuation
        : null;
}

/** Split a browser destination from the path React Router expects under its basename. */
export function browserNavigationTarget(path: string): { href: string; routerPath: string | null } {
    const href = scopedBrowserPath(path);
    const target = new URL(href, window.location.href);
    if (target.origin !== window.location.origin) return { href, routerPath: null };
    const destination = tenantRoute(target.pathname);
    const current = tenantRoute();
    return {
        href: `${target.pathname}${target.search}${target.hash}`,
        routerPath:
            destination.basename === current.basename
                ? `${destination.pathname}${target.search}${target.hash}`
                : null,
    };
}
